/**
 * RefundsAdmin.jsx — FM5A Staff Refund Workspace
 * Authorization: FM5_IMPLEMENTATION_PLAN.md v2 §11.2
 *
 * Freeze gate: refund execution is DISTINCT from FM3 payment reversal.
 * INV-FM5-REFUND-02: posting a refund does NOT mutate payment/allocation records.
 * INV-FM5-REFUND-03: governance disclosure when collectible_debt > 0 at approval.
 *
 * Tabs:
 *  1. Requests       — all refund requests; initiate (finance.refund.initiate)
 *  2. Approval Queue — UNDER_REVIEW; approve/reject (finance.refund.approve)
 *  3. Execution Queue— APPROVED awaiting execution (finance.refund.execute)
 *  4. History        — terminal states (APPROVED+executed, REJECTED, WITHDRAWN)
 */

import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import {
  RefreshCw, Plus, ChevronRight, ChevronDown, AlertTriangle,
  CheckCircle, XCircle, Clock, Banknote, FileText, Shield
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

function authHeaders() {
  const t = localStorage.getItem("academiaos_token");
  return { Authorization: `Bearer ${t}`, "Content-Type": "application/json" };
}

const fmt = (v) => {
  const n = parseFloat(v || 0);
  return isNaN(n) ? "₹0.00" : `₹${n.toLocaleString("en-IN", { minimumFractionDigits: 2 })}`;
};

const STATUS_COLORS = {
  INITIATED:    "bg-slate-50 text-slate-600 border-slate-200",
  UNDER_REVIEW: "bg-amber-50 text-amber-700 border-amber-200",
  APPROVED:     "bg-emerald-50 text-emerald-700 border-emerald-200",
  REJECTED:     "bg-red-50 text-red-700 border-red-200",
  WITHDRAWN:    "bg-slate-100 text-slate-500 border-slate-200",
};

function StatusBadge({ status }) {
  const cls = STATUS_COLORS[status] || "bg-slate-50 text-slate-600 border-slate-200";
  return (
    <span className={`px-2 py-0.5 text-xs font-medium border rounded ${cls}`}>
      {status?.replace(/_/g, " ")}
    </span>
  );
}

function TierBadge({ tier }) {
  if (!tier) return null;
  return (
    <span className={`px-2 py-0.5 text-xs font-medium border rounded ${
      tier === "MANAGER_APPROVE"
        ? "bg-rose-50 text-rose-700 border-rose-200"
        : "bg-blue-50 text-blue-700 border-blue-200"
    }`}>
      {tier === "MANAGER_APPROVE" ? "Manager Approval" : "Self-Approve"}
    </span>
  );
}

// ── INV-FM5-REFUND-03: Governance Disclosure Banner ──────────────────────────
function GovernanceDisclosure({ disclosure }) {
  if (!disclosure) return null;
  return (
    <div className="border border-amber-200 bg-amber-50 rounded-lg p-3 flex gap-2 mb-4">
      <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
      <div className="text-sm text-amber-800">
        <p className="font-semibold mb-1">Outstanding Debt Disclosure (INV-FM5-REFUND-03)</p>
        <p>Collectible debt at approval: <strong>{fmt(disclosure.collectible_debt_at_approval)}</strong></p>
        <p>Refund requested: <strong>{fmt(disclosure.refund_requested)}</strong></p>
        <p className="mt-1 text-amber-700">{disclosure.message}</p>
        <p className="mt-1 font-medium text-amber-800">
          Approving this refund does NOT reduce the student's outstanding liability.
        </p>
      </div>
    </div>
  );
}

// ── Initiate Modal ────────────────────────────────────────────────────────────
function InitiateModal({ institutionId, onClose, onDone }) {
  const [form, setForm] = useState({
    student_account_id: "", reason_code: "OVERPAYMENT",
    requested_amount: "", linked_payment_id: "", reason_description: "",
  });
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!form.student_account_id || !form.requested_amount) {
      toast.error("Student account ID and amount are required");
      return;
    }
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/finance/fm5/refunds?institution_id=${institutionId}`, {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify({ ...form, requested_amount: parseFloat(form.requested_amount) }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Failed to create refund request");
      toast.success("Refund request created (INITIATED)");
      onDone();
      onClose();
    } catch (e) { toast.error(e.message); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="initiate-refund-modal">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
        <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
          <h3 className="font-semibold text-slate-800">Initiate Refund Request</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><XCircle className="w-4 h-4" /></button>
        </div>
        <div className="p-5 space-y-3">
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Student Account ID *</label>
            <input data-testid="refund-student-account-id"
              className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
              value={form.student_account_id} onChange={e => setForm(f => ({...f, student_account_id: e.target.value}))} />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Reason Code *</label>
            <select data-testid="refund-reason-code"
              className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
              value={form.reason_code} onChange={e => setForm(f => ({...f, reason_code: e.target.value}))}>
              <option value="OVERPAYMENT">Overpayment</option>
              <option value="PROGRAMME_WITHDRAWAL">Programme Withdrawal</option>
              <option value="DUPLICATE_PAYMENT">Duplicate Payment</option>
              <option value="FEE_REDUCTION_RETROACTIVE">Fee Reduction Retroactive</option>
              <option value="CANCELLED_ENROLLMENT">Cancelled Enrollment</option>
              <option value="OTHER">Other</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Amount (INR) *</label>
            <input data-testid="refund-amount"
              type="number" min="0.01" className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
              value={form.requested_amount} onChange={e => setForm(f => ({...f, requested_amount: e.target.value}))} />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Linked Payment ID (optional at initiation)</label>
            <input data-testid="refund-payment-id"
              className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
              placeholder="UUID of the source payment"
              value={form.linked_payment_id} onChange={e => setForm(f => ({...f, linked_payment_id: e.target.value}))} />
          </div>
          {form.reason_code === "OTHER" && (
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Description *</label>
              <textarea className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
                rows={2} value={form.reason_description}
                onChange={e => setForm(f => ({...f, reason_description: e.target.value}))} />
            </div>
          )}
          <div className="text-xs text-slate-500 bg-slate-50 rounded p-2">
            <strong>Note:</strong> This creates a refund REQUEST (INITIATED). It does NOT reverse any FM3 payment.
            Refund execution is a separate, governed step after approval.
          </div>
        </div>
        <div className="px-5 pb-4 flex justify-end gap-2">
          <button onClick={onClose} className="px-3 py-1.5 text-sm border border-slate-200 rounded-lg">Cancel</button>
          <button data-testid="initiate-refund-submit" onClick={submit} disabled={saving}
            className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg disabled:opacity-50">
            {saving ? "Creating…" : "Create Request"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Approve Modal ─────────────────────────────────────────────────────────────
function ApproveModal({ req, institutionId, onClose, onDone }) {
  const [paymentId, setPaymentId] = useState(req.linked_payment_id || "");
  const [disclosure, setDisclosure] = useState(null);
  const [saving, setSaving] = useState(false);

  const approve = async () => {
    if (!paymentId.trim()) { toast.error("linked_payment_id is required before approval (INV-FM5-REFUND-01)"); return; }
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/finance/fm5/refunds/${req.id}/approve?institution_id=${institutionId}`, {
        method: "PATCH",
        headers: authHeaders(),
        body: JSON.stringify({ linked_payment_id: paymentId }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Approval failed");
      if (data.disclosure) setDisclosure(data.disclosure);
      toast.success(`Approved (${data.approval_tier})`);
      onDone();
      if (!data.disclosure) onClose();
    } catch (e) { toast.error(e.message); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="approve-refund-modal">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
        <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
          <h3 className="font-semibold text-slate-800">Approve Refund Request</h3>
          <button onClick={onClose}><XCircle className="w-4 h-4 text-slate-400" /></button>
        </div>
        <div className="p-5 space-y-3">
          <div className="text-sm text-slate-600">
            Amount: <strong className="text-slate-800">{fmt(req.requested_amount)}</strong> ·
            Reason: <strong>{req.reason_code?.replace(/_/g, " ")}</strong>
          </div>
          <GovernanceDisclosure disclosure={disclosure} />
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">
              Linked Payment ID * (INV-FM5-REFUND-01: required before approval)
            </label>
            <input data-testid="approve-payment-id"
              className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
              value={paymentId} onChange={e => setPaymentId(e.target.value)} />
          </div>
          <div className="text-xs text-slate-500 bg-blue-50 border border-blue-100 rounded p-2">
            <Shield className="w-3 h-3 inline mr-1 text-blue-500" />
            Approving this refund does NOT reverse the linked FM3 payment record. (INV-FM5-REFUND-02)
          </div>
        </div>
        <div className="px-5 pb-4 flex justify-end gap-2">
          <button onClick={onClose} className="px-3 py-1.5 text-sm border border-slate-200 rounded-lg">Cancel</button>
          <button data-testid="approve-refund-confirm" onClick={approve} disabled={saving}
            className="px-3 py-1.5 text-sm bg-emerald-600 text-white rounded-lg disabled:opacity-50">
            {saving ? "Approving…" : "Approve"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Execute Modal ─────────────────────────────────────────────────────────────
function ExecuteModal({ req, institutionId, onClose, onDone }) {
  const [form, setForm] = useState({ refund_method: "BANK_TRANSFER", notes: "" });
  const [saving, setSaving] = useState(false);

  const execute = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/finance/fm5/refunds/${req.id}/execute?institution_id=${institutionId}`, {
        method: "PATCH",
        headers: authHeaders(),
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Execution failed");
      toast.success(`Refund execution record created (PENDING). ${data.invariant_note}`);
      onDone();
      onClose();
    } catch (e) { toast.error(e.message); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="execute-refund-modal">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
        <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
          <h3 className="font-semibold text-slate-800">Execute Refund Disbursement</h3>
          <button onClick={onClose}><XCircle className="w-4 h-4 text-slate-400" /></button>
        </div>
        <div className="p-5 space-y-3">
          <div className="text-sm text-slate-600">Amount: <strong>{fmt(req.requested_amount)}</strong></div>
          <div className="bg-blue-50 border border-blue-100 rounded p-2 text-xs text-blue-800">
            <Shield className="w-3 h-3 inline mr-1" />
            <strong>INV-FM5-REFUND-02:</strong> Executing this refund creates a new outbound disbursement record.
            It does NOT alter the original FM3 payment record, payment_allocation, or any receipt.
            This is a new financial event, not a payment reversal.
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Refund Method</label>
            <select data-testid="execute-refund-method"
              className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
              value={form.refund_method} onChange={e => setForm(f => ({...f, refund_method: e.target.value}))}>
              <option>BANK_TRANSFER</option>
              <option>CHEQUE</option>
              <option>CASH</option>
              <option>CARD</option>
              <option>ONLINE</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Notes (optional)</label>
            <textarea rows={2} className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
              value={form.notes} onChange={e => setForm(f => ({...f, notes: e.target.value}))} />
          </div>
        </div>
        <div className="px-5 pb-4 flex justify-end gap-2">
          <button onClick={onClose} className="px-3 py-1.5 text-sm border border-slate-200 rounded-lg">Cancel</button>
          <button data-testid="execute-refund-confirm" onClick={execute} disabled={saving}
            className="px-3 py-1.5 text-sm bg-indigo-600 text-white rounded-lg disabled:opacity-50">
            {saving ? "Processing…" : "Execute Disbursement"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Reject Modal ──────────────────────────────────────────────────────────────
function RejectModal({ req, institutionId, onClose, onDone }) {
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);

  const reject = async () => {
    if (!reason.trim()) { toast.error("Rejection reason is required"); return; }
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/finance/fm5/refunds/${req.id}/reject?institution_id=${institutionId}`, {
        method: "PATCH", headers: authHeaders(),
        body: JSON.stringify({ rejection_reason: reason }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Rejection failed");
      toast.success("Refund request rejected");
      onDone(); onClose();
    } catch (e) { toast.error(e.message); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-sm">
        <div className="px-5 py-4 border-b border-slate-100">
          <h3 className="font-semibold text-slate-800">Reject Refund Request</h3>
        </div>
        <div className="p-5 space-y-3">
          <textarea data-testid="reject-reason-input" rows={3} placeholder="Rejection reason (required)"
            className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
            value={reason} onChange={e => setReason(e.target.value)} />
        </div>
        <div className="px-5 pb-4 flex justify-end gap-2">
          <button onClick={onClose} className="px-3 py-1.5 text-sm border border-slate-200 rounded-lg">Cancel</button>
          <button data-testid="reject-refund-confirm" onClick={reject} disabled={saving}
            className="px-3 py-1.5 text-sm bg-red-600 text-white rounded-lg disabled:opacity-50">
            {saving ? "Rejecting…" : "Reject"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Refund Request Row ────────────────────────────────────────────────────────
function RefundRow({ req, canApprove, canExecute, canInitiate, institutionId, onRefresh }) {
  const [modal, setModal] = useState(null); // approve | execute | reject

  const withdraw = async () => {
    try {
      const res = await fetch(`${API}/api/finance/fm5/refunds/${req.id}/withdraw?institution_id=${institutionId}`,
        { method: "PATCH", headers: authHeaders() });
      if (!res.ok) { const d = await res.json(); throw new Error(d.detail); }
      toast.success("Withdrawn"); onRefresh();
    } catch (e) { toast.error(e.message); }
  };

  const submit = async () => {
    try {
      const res = await fetch(`${API}/api/finance/fm5/refunds/${req.id}/submit?institution_id=${institutionId}`,
        { method: "PATCH", headers: authHeaders() });
      if (!res.ok) { const d = await res.json(); throw new Error(d.detail); }
      toast.success("Submitted for review"); onRefresh();
    } catch (e) { toast.error(e.message); }
  };

  return (
    <>
      <tr className="border-b border-slate-100 hover:bg-slate-50 text-sm" data-testid={`refund-row-${req.id}`}>
        <td className="px-4 py-3 font-mono text-xs text-slate-500">{req.id.slice(0,8)}…</td>
        <td className="px-4 py-3 font-mono text-xs text-slate-500">{req.student_account_id?.slice(0,8)}…</td>
        <td className="px-4 py-3"><StatusBadge status={req.status} /></td>
        <td className="px-4 py-3 text-right font-medium">{fmt(req.requested_amount)}</td>
        <td className="px-4 py-3 text-slate-500 text-xs">{req.reason_code?.replace(/_/g," ")}</td>
        <td className="px-4 py-3"><TierBadge tier={req.approval_tier} /></td>
        <td className="px-4 py-3 text-xs text-slate-400">{req.created_at ? new Date(req.created_at).toLocaleDateString("en-IN") : "—"}</td>
        <td className="px-4 py-3">
          <div className="flex gap-1 flex-wrap">
            {req.status === "INITIATED" && canInitiate && (
              <button onClick={submit} data-testid={`refund-submit-btn-${req.id}`}
                className="px-2 py-1 text-xs bg-amber-50 text-amber-700 border border-amber-200 rounded hover:bg-amber-100">
                Submit for Review
              </button>
            )}
            {req.status === "UNDER_REVIEW" && canApprove && (
              <>
                <button onClick={() => setModal("approve")} data-testid={`refund-approve-btn-${req.id}`}
                  className="px-2 py-1 text-xs bg-emerald-50 text-emerald-700 border border-emerald-200 rounded hover:bg-emerald-100">Approve</button>
                <button onClick={() => setModal("reject")} data-testid={`refund-reject-btn-${req.id}`}
                  className="px-2 py-1 text-xs bg-red-50 text-red-600 border border-red-200 rounded hover:bg-red-100">Reject</button>
              </>
            )}
            {req.status === "APPROVED" && !req.refund_id && canExecute && (
              <button onClick={() => setModal("execute")} data-testid={`refund-execute-btn-${req.id}`}
                className="px-2 py-1 text-xs bg-indigo-50 text-indigo-700 border border-indigo-200 rounded hover:bg-indigo-100">Execute</button>
            )}
            {["INITIATED","UNDER_REVIEW"].includes(req.status) && canInitiate && (
              <button onClick={withdraw} className="px-2 py-1 text-xs bg-slate-50 text-slate-500 border border-slate-200 rounded hover:bg-slate-100">Withdraw</button>
            )}
          </div>
        </td>
      </tr>
      {modal === "approve" && <tr><td colSpan={8}>{
        <ApproveModal req={req} institutionId={institutionId} onClose={() => setModal(null)} onDone={onRefresh} />
      }</td></tr>}
      {modal === "execute" && <tr><td colSpan={8}>{
        <ExecuteModal req={req} institutionId={institutionId} onClose={() => setModal(null)} onDone={onRefresh} />
      }</td></tr>}
      {modal === "reject" && <tr><td colSpan={8}>{
        <RejectModal req={req} institutionId={institutionId} onClose={() => setModal(null)} onDone={onRefresh} />
      }</td></tr>}
    </>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────
export default function RefundsAdmin() {
  const { user } = useAuth();
  const [tab, setTab] = useState("requests");
  const [refunds, setRefunds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInitiate, setShowInitiate] = useState(false);

  const institutionId = user?.institution_id;
  const role = user?.role;

  const canInitiate = ["finance_officer","finance_manager","cfo","institution_admin","super_admin"].includes(role);
  const canApprove  = ["finance_manager","cfo","institution_admin","super_admin","finance_officer"].includes(role);
  const canExecute  = ["finance_officer","finance_manager","cfo","institution_admin","super_admin"].includes(role);

  const load = useCallback(async () => {
    if (!institutionId) return;
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/finance/fm5/refunds?institution_id=${institutionId}`, { headers: authHeaders() });
      const data = await res.json();
      setRefunds(Array.isArray(data) ? data : []);
    } catch { toast.error("Failed to load refund requests"); }
    finally { setLoading(false); }
  }, [institutionId]);

  useEffect(() => { load(); }, [load]);

  const filterByTab = (tab) => {
    switch (tab) {
      case "approval":   return refunds.filter(r => r.status === "UNDER_REVIEW");
      case "execution":  return refunds.filter(r => r.status === "APPROVED" && !r.refund_id);
      case "history":    return refunds.filter(r => ["REJECTED","WITHDRAWN"].includes(r.status) || (r.status === "APPROVED" && r.refund_id));
      default:           return refunds;
    }
  };

  const displayed = filterByTab(tab);
  const tabs = [
    { id: "requests",  label: "All Requests", count: refunds.length },
    { id: "approval",  label: "Approval Queue", count: refunds.filter(r=>r.status==="UNDER_REVIEW").length },
    { id: "execution", label: "Execution Queue", count: refunds.filter(r=>r.status==="APPROVED"&&!r.refund_id).length },
    { id: "history",   label: "History" },
  ];

  return (
    <div className="p-6 space-y-5" data-testid="refunds-admin-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Refunds</h1>
          <p className="text-sm text-slate-500 mt-0.5">
            FM5A — Refund request lifecycle. Execution is distinct from FM3 payment reversal.
          </p>
        </div>
        <div className="flex gap-2">
          <button onClick={load} className="p-2 border border-slate-200 rounded-lg hover:bg-slate-50" data-testid="refunds-refresh-btn">
            <RefreshCw className={`w-4 h-4 text-slate-500 ${loading ? "animate-spin" : ""}`} />
          </button>
          {canInitiate && (
            <button onClick={() => setShowInitiate(true)} data-testid="initiate-refund-btn"
              className="flex items-center gap-2 px-3 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700">
              <Plus className="w-4 h-4" /> Initiate Request
            </button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-slate-200 gap-1">
        {tabs.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} data-testid={`refunds-tab-${t.id}`}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              tab === t.id
                ? "border-blue-500 text-blue-600"
                : "border-transparent text-slate-500 hover:text-slate-700"
            }`}>
            {t.label}
            {t.count > 0 && (
              <span className="ml-1.5 px-1.5 py-0.5 text-xs bg-slate-100 text-slate-600 rounded-full">{t.count}</span>
            )}
          </button>
        ))}
      </div>

      {/* Content */}
      {loading ? (
        <div className="text-center py-12 text-slate-400" data-testid="refunds-loading">Loading…</div>
      ) : displayed.length === 0 ? (
        <div className="text-center py-12 text-slate-400" data-testid="refunds-empty">
          <Banknote className="w-8 h-8 mx-auto mb-2 opacity-40" />
          <p>No refund requests{tab !== "requests" ? ` in ${tab}` : ""}</p>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-slate-200">
          <table className="w-full text-left" data-testid="refunds-table">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr className="text-xs font-medium text-slate-500 uppercase tracking-wider">
                <th className="px-4 py-3">ID</th>
                <th className="px-4 py-3">Student Account</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3 text-right">Amount</th>
                <th className="px-4 py-3">Reason</th>
                <th className="px-4 py-3">Tier</th>
                <th className="px-4 py-3">Created</th>
                <th className="px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {displayed.map(r => (
                <RefundRow key={r.id} req={r}
                  canApprove={canApprove} canExecute={canExecute} canInitiate={canInitiate}
                  institutionId={institutionId} onRefresh={load} />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showInitiate && (
        <InitiateModal institutionId={institutionId} onClose={() => setShowInitiate(false)} onDone={load} />
      )}
    </div>
  );
}
